// Constants
const STORAGE_KEY = 'mermaid-flow-diagrams';

/**
 * Get all saved diagrams from localStorage
 * @returns {Promise<Array>} Array of diagram objects
 */
export const getDiagrams = async () => {
  try {
    const storedDiagrams = localStorage.getItem(STORAGE_KEY);
    return storedDiagrams ? JSON.parse(storedDiagrams) : [];
  } catch (error) {
    console.error('Error getting diagrams from localStorage:', error);
    return [];
  }
};

/**
 * Get a diagram by ID
 * @param {string} id - Diagram ID
 * @returns {Promise<Object|null>} Diagram object or null if not found
 */
export const getDiagramById = async (id) => {
  try {
    const diagrams = await getDiagrams();
    return diagrams.find(diagram => diagram.id === id) || null;
  } catch (error) {
    console.error(`Error getting diagram with ID ${id}:`, error);
    return null;
  }
};

/**
 * Store a diagram in localStorage
 * @param {Object} diagram - Diagram object to store
 * @returns {Promise<Object>} Stored diagram object
 */
export const storeDiagram = async (diagram) => {
  try {
    const diagrams = await getDiagrams();
    const existingIndex = diagrams.findIndex(d => d.id === diagram.id);
    
    if (existingIndex >= 0) {
      // Update existing diagram
      diagrams[existingIndex] = {
        ...diagrams[existingIndex],
        ...diagram,
        updatedAt: new Date().toISOString(),
      };
    } else {
      // Add new diagram
      diagrams.push({
        ...diagram,
        createdAt: diagram.createdAt || new Date().toISOString(),
        updatedAt: diagram.updatedAt || new Date().toISOString(),
      });
    }
    
    // Sort by updatedAt (newest first)
    diagrams.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(diagrams));
    
    return diagram;
  } catch (error) {
    console.error('Error storing diagram:', error);
    throw error;
  }
};

/**
 * Delete a diagram by ID
 * @param {string} id - Diagram ID
 * @returns {Promise<boolean>} True if deleted, false otherwise
 */
export const deleteDiagram = async (id) => {
  try {
    const diagrams = await getDiagrams();
    const filteredDiagrams = diagrams.filter(diagram => diagram.id !== id);
    
    if (filteredDiagrams.length === diagrams.length) {
      // Diagram not found
      return false;
    }
    
    // Store filtered diagrams in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredDiagrams));
    
    return true;
  } catch (error) {
    console.error(`Error deleting diagram with ID ${id}:`, error);
    return false;
  }
};

/**
 * Export diagrams to a JSON file
 * @returns {Promise<string>} JSON string of all diagrams
 */
export const exportDiagrams = async () => {
  try {
    const diagrams = await getDiagrams();
    return JSON.stringify(diagrams, null, 2);
  } catch (error) {
    console.error('Error exporting diagrams:', error);
    throw error;
  }
};

/**
 * Import diagrams from a JSON string
 * @param {string} jsonString - JSON string of diagrams
 * @param {boolean} replace - Whether to replace existing diagrams
 * @returns {Promise<Array>} Array of imported diagram objects
 */
export const importDiagrams = async (jsonString, replace = false) => {
  try {
    const importedDiagrams = JSON.parse(jsonString);
    
    if (!Array.isArray(importedDiagrams)) {
      throw new Error('Invalid diagram data format');
    }
    
    if (replace) {
      // Replace all diagrams
      localStorage.setItem(STORAGE_KEY, jsonString);
      return importedDiagrams;
    } else {
      // Merge with existing diagrams
      const existingDiagrams = await getDiagrams();
      const mergedDiagrams = [...existingDiagrams];
      
      // Add or update imported diagrams
      for (const diagram of importedDiagrams) {
        const existingIndex = mergedDiagrams.findIndex(d => d.id === diagram.id);
        
        if (existingIndex >= 0) {
          // Update existing diagram
          mergedDiagrams[existingIndex] = {
            ...mergedDiagrams[existingIndex],
            ...diagram,
            updatedAt: new Date().toISOString(),
          };
        } else {
          // Add new diagram
          mergedDiagrams.push({
            ...diagram,
            updatedAt: new Date().toISOString(),
          });
        }
      }
      
      // Sort by updatedAt (newest first)
      mergedDiagrams.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      
      // Store in localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedDiagrams));
      
      return mergedDiagrams;
    }
  } catch (error) {
    console.error('Error importing diagrams:', error);
    throw error;
  }
}; 